import React, { useState, useMemo } from 'react';
import { TextField, useTheme, MenuItem, Checkbox, ListItemText, InputAdornment, Box, Button, IconButton } from '@mui/material';
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

const CustomeFormInputField = ({
  label,
  placeholder,
  labelSx = {},
  inputSx = {},
  options = [],
  multiline = false,
  rows = 4,
  select = false,
  multiple = false,
  required = false,
  register,
  searchable = false,
  searchHeight = 36,
  // searchable = false,
  showSubjectActions = false,
  onAddSubject,
  onDeleteSubject,
  // searchHeight = 36,
  name,
  borderColor,
  error,
  helperText,
  value,
  onChange,
  size = 'small',
  disabled = false,
  maxLength,
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddInput, setShowAddInput] = useState(false);
  const [newOption, setNewOption] = useState("");
  const [editingOption, setEditingOption] = useState(null);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm) return options;
    return options.filter((option) =>
      String(option.label).toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm, searchable]);

  const labelBackground = required
    ? isDark
      ? '#660000'
      : 'linear-gradient(135deg, #673ab7, #9575cd)'
    : isDark
      ? '#003366'
      : 'linear-gradient(135deg, #2196f3, #21cbf3)';

  const inputLabelProps = {
    shrink: true,
    sx: {
      background: disabled
        ? (isDark ? '#444444' : '#cccccc')
        : labelBackground,
      fontWeight: 700,
      clipPath: 'polygon(0% 0%, 100% 0%, 95% 100%, 5% 100%)',
      padding: '4px 14px',
      top: '-4px',
      borderRadius: '0',
      color: disabled
        ? (isDark ? '#aaa' : '#333')
        : '#fff',
      fontSize: '12px',
      '&.Mui-focused': {
        color: disabled
          ? (isDark ? '#aaa' : '#333')
          : '#fff',
      },
      '&.Mui-error': {
        color: disabled
          ? (isDark ? '#aaa' : '#333')
          : '#fff',
        '.MuiFormLabel-asterisk': {
          color: disabled
            ? (isDark ? '#aaa' : '#333')
            : '#fff',
        },
      },
      ...labelSx,
    },
  };


  const textFieldSx = {
    width: '100%',
    borderColor: borderColor || (disabled
      ? '#b0b0b0'
      : theme.palette.custom?.InputborderColor || theme.palette.divider),

    '& .MuiOutlinedInput-root': {
      //           '&:hover .MuiOutlinedInput-notchedOutline': {
      //   borderColor: theme.palette.custom?.InputborderColor, 
      // },
      overflow: 'hidden',
      borderColor: borderColor || (disabled
        ? '#b0b0b0'
        : theme.palette.custom?.InputborderColor || theme.palette.divider),
      borderRadius: '8px',
      height: multiline ? 'auto' : 'auto',
      backgroundColor: theme.palette.background.paper,
      color: disabled
        ? '#666666'
        : theme.palette.background?.textblack || theme.palette.text.primary,

      '& input': {
        padding: '10px 14px',
        opacity: '1',
        border: 'none',
        fontSize: '16px',
        backgroundColor: theme.palette.background.paper,
        color: disabled
          ? (isDark ? "#aaa" : "#333")
          : theme.palette.background?.textblack || theme.palette.text.primary,
        '&::placeholder': {
          fontSize: '14px',
          opacity: 1,
          color: isDark ? '#aaa' : '#333',
        },
        ...inputSx,
      },

      '& textarea': {
        padding: '0',
        '&::placeholder': {
          color: isDark ? '#aaa' : '#333',
          fontSize: '14px',
          opacity: 1,
        },
        ...inputSx,
      },

      '& .MuiSelect-select': {
        backgroundColor: theme.palette.background.paper,
        color: disabled
          ? (isDark ? "#aaa" : "#333")
          : theme.palette.background?.textblack || theme.palette.text.primary,
        padding: '10px 14px',
        '&::placeholder': {
          color: isDark ? '#aaa' : '#333',
          fontSize: '14px',
          opacity: 1,
        },
        ...inputSx,
      },

      '&:hover fieldset': {
        pointerEvents: 'none',
      },
      '&.Mui-focused fieldset': {
        borderColor: borderColor || (disabled
          ? '#b0b0b0'
          : theme.palette.custom?.InputborderColor || theme.palette.divider),
      },
    },
    '& fieldset': {
      padding: 0,
      borderRadius: '8px',
      top: 0,
      borderColor: borderColor || (disabled
        ? '#b0b0b0'
        : theme.palette.custom?.InputborderColor || theme.palette.divider),
    },
    '& fieldset legend': {
      display: 'none',
    },
  };

  const registerProps = register ? register(name) : {};
  const inputProps = {
    ...registerProps,
    ...(value !== undefined ? { value } : {}),
    onChange: onChange || registerProps.onChange,
    maxLength,
  };

  return (
    <TextField
      className={`autofill_input ${isDark ? 'darkmd' : 'lightmd'}`}
      size="small"
      label={label}
      placeholder={placeholder}
      variant="outlined"
      multiline={multiline}
      rows={multiline ? rows : undefined}
      select={select}
      required={required}
      InputLabelProps={inputLabelProps}
      sx={[textFieldSx, sx]}
      error={!!error}
      helperText={error ? helperText : ' '}
      disabled={disabled}
      inputProps={inputProps}
      FormHelperTextProps={{
        sx: {
          position: 'absolute',
          top: '-16px',
          right: '8px',
          color: 'red',
          fontSize: '10px',
          margin: 0,
        },
      }}
      SelectProps={{
        multiple: multiple,
        renderValue: selected => {
          if (multiple) {
            return options
              .filter(opt => selected.includes(opt.value))
              .map(opt => opt.label)
              .join(', ');
          } else {
            const selectedOption = options.find(opt => opt.value === selected);
            return selectedOption ? selectedOption.label : '';
          }
        },
        MenuProps: {
          PaperProps: {
            style: {
              maxHeight: 350,
              // minHeight: 150,  
              // width: 250,      
            },
          },
        },
        ...props.SelectProps,
      }}
      // SelectProps={{
      //   multiple: multiple,
      //   renderValue: (selected) =>
      //     multiple
      //       ? options
      //         .filter((opt) => selected.includes(opt.value))
      //         .map((opt) => opt.label)
      //         .join(', ')
      //       : selected,
      //   ...props.SelectProps,
      // }}

      {...inputProps}
      {...props}
    >

      {showSubjectActions && showAddInput && (
        <Box
          sx={{
            p: 2,
            pt: 1,
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            // borderBottom: `1px solid ${theme.palette.divider}`,
          }}
          // onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Enter subject"
            value={newOption}
            // onChange={(e) => setNewOption(e.target.value)}
            onChange={(e) => setNewOption(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            onKeyUp={(e) => {
              e.stopPropagation();
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: searchHeight,
                minHeight: searchHeight,
                display: "flex",
                alignItems: "center",
                border: `1px solid ${theme.palette.custom.borderColor}`,
                borderRadius: '8px',
                padding: "1px 8px",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: `1px solid ${theme.palette.custom.borderColor}`,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: `1px solid ${theme.palette.custom.borderColor}`,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: `1px solid ${theme.palette.custom.borderColor}`,
                },
              },
            }}
          />
          <Box sx={{ display: "flex", gap: 0 }}>
            <IconButton
              style={{ color: theme.palette.background.bggreen }}
              title="Save"
              size="small"
              // onClick={() => {
              //   setShowAddInput(false);
              //   setEditingOption(null);
              //   setNewOption("");
              // }}
              onClick={async () => {
                if (!newOption.trim()) return;

                await onAddSubject?.({
                  subjectID: editingOption?.subjectID,
                  subjectName: newOption,
                });

                setShowAddInput(false);
                setEditingOption(null);
                setNewOption("");
              }}
            >
              <i class="fa-sharp fa-regular fa-check"></i>
            </IconButton>
            <IconButton
              sx={{ color: theme.palette.background.bgred, }}
              title="Cancel"
              size="small"
              onClick={() => {
                setShowAddInput(false);
                setEditingOption(null);
                setNewOption("");
              }}
            //       onClick={() => {
            //   if (!newOption.trim()) return;

            //   if (editingOption) {
            //     console.log("Update Option");
            //   } else {
            //     console.log("Add Option");
            //   }

            //   setShowAddInput(false);
            //   setEditingOption(null);
            //   setNewOption("");
            // }}
            >
              <i class="fa-sharp fa-regular fa-xmark"
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  if (editingOption?.subjectID) {
                    await onDeleteSubject?.(editingOption.subjectID);

                  }
                  setNewOption("");
                  setEditingOption(null);
                  setShowAddInput(false);
                }}></i>
            </IconButton>
          </Box>

        </Box>
      )}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.5,
        py:  showAddInput ? 0 : 1,
        px: 2,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        {searchable && (!showSubjectActions || !showAddInput) && (
          <Box sx={{ width: '100%' }}>
            <TextField
              fullWidth
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {/* <SearchIcon fontSize="small" /> */}
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: searchHeight,
                  minHeight: searchHeight,
                  // backgroundColor: isDark ? "#333" : "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  border: `1px solid ${theme.palette.custom.borderColor}`,
                  borderRadius: '8px',
                  padding: "1px 8px",
                  //backgroundColor: theme.palette.background.paper,
                  // color: effectiveTextColor,
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: `1px solid ${theme.palette.custom.borderColor}`, // 🔁 Your custom border color
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: `1px solid ${theme.palette.custom.borderColor}`,// Hover color
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: `1px solid ${theme.palette.custom.borderColor}`, // Focused color
                  },
                },
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        )}
        {showSubjectActions && !showAddInput && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              pt: 0,
              width: 150,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              size="small"
              sx={{ fontSize: 12 }}
              // startIcon={<AddIcon />}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowAddInput(true);
                setEditingOption(null);
                setNewOption("");
              }}
            >
              <i class="fa-sharp fa-regular fa-plus"></i> Add Subject
            </Button>
          </Box>
        )}
      </Box>
      {filteredOptions.length === 0 ? (
        <MenuItem disabled>No options found</MenuItem>
      ) : (
        filteredOptions.map((option, index) => (
          <MenuItem key={index} value={option.value}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: 1,
              }}
            >
              {multiple && (
                <Checkbox
                  checked={value?.includes(option.value)}
                  sx={{
                    color: theme.palette.mode === 'light' ? '#d0d0d0' : '#EBF2F5',
                    '&.Mui-checked': {
                      color: '#8f01ff',
                    },
                    '&.MuiCheckbox-indeterminate': {
                      color: '#8f01ff',
                    },
                  }}
                />
              )}
              <ListItemText primary={option.label} />
              {showSubjectActions && (
                <EditIcon
                  fontSize="small"
                  sx={{
                    fontSize: 18,
                    color: "primary.main",
                    cursor: "pointer",
                  }}
                  // onClick={(e) => {
                  //   e.stopPropagation();

                  //   setShowAddInput(true);
                  //   setEditingOption(option);
                  //   setNewOption(option.label);
                  // }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    setShowAddInput(true);
                    setEditingOption(option);
                    setNewOption(option.label);
                  }}
                />
              )}
            </Box>
          </MenuItem>
        ))
      )
      }
    </TextField>
  );
};

export default CustomeFormInputField;
